import { useRef, useState } from 'react'
import { formatarCnpj, normalizarCnpj, validarCnpj } from '@precad/compartilhado/validacao/cnpj'
import { chamar, exigirSucesso } from '../api'
import { Campo } from '../componentes/Campo'
import { Turnstile, type TurnstileHandle } from '../componentes/Turnstile'
import { ACAO_ACESSO, TURNSTILE_SITEKEY } from '../config'
import type { Acompanhamento } from '../tipos'

/**
 * Acompanhamento da solicitação: CNPJ → código por e-mail → painel.
 *
 * É o ÚNICO lugar do sistema que cobra código, e a razão é o que ele mostra:
 * decisão de crédito e motivo de reprovação. O link do e-mail de correção pode
 * dispensar código porque só quem foi devolvido o recebe e ele deixa de valer
 * junto com o status; uma consulta permanente aberta por link vazaria a cada
 * encaminhamento.
 *
 * O código vai para o e-mail JÁ CADASTRADO — nunca para um informado aqui.
 * Sem isso a tela seria um oráculo: quem soubesse o CNPJ de uma farmácia, que
 * é dado público, leria o motivo da reprovação dela.
 */

type Fase = 'cnpj' | 'codigo' | 'painel'

const ROTULO_STATUS: Record<string, string> = {
  'Em Análise de Crédito': 'Em análise',
  'Aguardando Correção': 'Aguardando seu ajuste',
  Aprovado: 'Aprovado',
  Reprovado: 'Não aprovado',
  Concluído: 'Cadastro ativo',
  Cancelado: 'Cancelada',
}

/* Cor do selo. Nem tudo que não é aprovação é vermelho: "aguardando ajuste" é
   uma pendência com o cliente, não uma recusa, e pintá-la de erro faria a
   pessoa achar que foi reprovada. */
const TOM_STATUS: Record<string, string> = {
  'Em Análise de Crédito': 'selo--neutro',
  'Aguardando Correção': 'selo--atencao',
  Aprovado: 'selo--ok',
  Reprovado: 'selo--erro',
  Concluído: 'selo--ok',
  Cancelado: 'selo--neutro',
}

function formatarData(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function Acompanhar({
  aoVoltar,
  aoAbrirFicha,
}: {
  aoVoltar: () => void
  /** Sobe para o App: a ficha é tela cheia, não um bloco dentro do painel. */
  aoAbrirFicha: () => void
}) {
  const [fase, setFase] = useState<Fase>('cnpj')
  const [cnpj, setCnpj] = useState('')
  const [codigo, setCodigo] = useState('')
  const [token, setToken] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [dados, setDados] = useState<Acompanhamento | null>(null)

  const turnstile = useRef<TurnstileHandle | null>(null)
  const cnpjNorm = normalizarCnpj(cnpj)

  async function pedirCodigo(evento: React.FormEvent) {
    evento.preventDefault()
    if (ocupado) return

    if (!validarCnpj(cnpjNorm)) {
      setErro(cnpjNorm ? 'Este CNPJ não é válido. Confira os números.' : 'Informe o CNPJ da empresa.')
      return
    }
    if (!token) {
      setErro('Aguarde a verificação de segurança terminar, logo abaixo.')
      return
    }

    setOcupado(true)
    setErro(null)
    try {
      exigirSucesso(
        await chamar('solicitarAcesso', { dados: { cnpj: cnpjNorm }, turnstileToken: token }),
      )
      setFase('codigo')
    } catch (e) {
      setErro((e as Error).message)
      /* Token do Turnstile é de uso único: sem reiniciar, a segunda tentativa
         falharia por um motivo diferente do que a pessoa acabou de ler. */
      turnstile.current?.reiniciar()
      setToken(null)
    } finally {
      setOcupado(false)
    }
  }

  async function entrar(evento: React.FormEvent) {
    evento.preventDefault()
    if (ocupado) return

    setOcupado(true)
    setErro(null)
    try {
      exigirSucesso(await chamar('validarAcesso', { dados: { cnpj: cnpjNorm, codigo } }))
      const linhas = await chamar<Acompanhamento>('acompanhar')
      const linha = linhas[0]
      if (!linha || linha.success !== 1) throw new Error('Não foi possível abrir sua solicitação.')
      setDados(linha)
      setFase('painel')
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setOcupado(false)
    }
  }

  /* ---------------------------------------------------------------- painel */
  if (fase === 'painel' && dados) {
    return (
      <div className="grade">
        <div className="acomp__topo">
          <div>
            <span className="acomp__rotulo">Solicitação</span>
            <span className="acomp__numero">{dados.numero ?? '—'}</span>
          </div>
          <span className={`selo ${TOM_STATUS[dados.status] ?? 'selo--neutro'}`}>
            {ROTULO_STATUS[dados.status] ?? dados.status}
          </span>
        </div>

        <p className="acomp__empresa">
          {dados.razaoSocial ?? 'Sua empresa'}
          {dados.cnpj ? ` · ${formatarCnpj(dados.cnpj)}` : ''}
        </p>

        {dados.pendencias.length > 0 ? (
          <div className="aviso aviso--correcao">
            <h2 className="aviso__titulo">O que precisamos que você ajuste</h2>
            <ul className="aviso__lista">
              {dados.pendencias.map((p, i) => (
                <li key={i}>
                  <strong>{p.documento ?? p.campo ?? 'Cadastro'}</strong> — {p.motivo}
                </li>
              ))}
            </ul>
            <p className="aviso__nota">
              Abra o link que enviamos por e-mail para corrigir e reenviar.
            </p>
          </div>
        ) : null}

        {dados.status === 'Reprovado' && dados.motivoDecisao ? (
          <div className="aviso aviso--erro">
            <span>
              <strong>Motivo:</strong> {dados.motivoDecisao}
            </span>
          </div>
        ) : null}

        {dados.codigoErp ? (
          <div className="acomp__destaque">
            <span className="acomp__rotulo">Código de cliente</span>
            <span className="acomp__numero">{dados.codigoErp}</span>
            <span className="acomp__nota">Informe este código ao fazer seus pedidos.</span>
          </div>
        ) : null}

        {/* A ficha só aparece quando existe. `Dispensada` e `Não Liberada` não
            mostram nada — oferecer um botão que abre uma mensagem de "ainda
            não" é pior do que não oferecer botão. */}
        {dados.statusFicha !== 'Não Liberada' && dados.statusFicha !== 'Dispensada' ? (
          <div className="acomp__ficha">
            <div>
              <span className="acomp__rotulo">Ficha cadastral</span>
              <span className="acomp__fichaStatus">
                {dados.statusFicha === 'Aguardando Assinatura'
                  ? 'Falta assinar e enviar'
                  : dados.statusFicha === 'Recebida'
                    ? 'Recebida, em conferência'
                    : dados.statusFicha === 'Validada'
                      ? 'Conferida e aceita'
                      : 'Recusada — precisa reenviar'}
              </span>
            </div>
            <button type="button" className="botao" onClick={aoAbrirFicha}>
              {dados.statusFicha === 'Validada' ? 'Ver ficha' : 'Abrir ficha'}
            </button>
          </div>
        ) : null}

        {dados.linhaDoTempo.length > 0 ? (
          <ol className="linha">
            {dados.linhaDoTempo.map((e, i) => (
              <li key={i} className="linha__item">
                <span className="linha__data">{formatarData(e.em)}</span>
                <span className="linha__texto">{e.titulo}</span>
              </li>
            ))}
          </ol>
        ) : null}

        {dados.prazoAnalise && dados.status === 'Em Análise de Crédito' ? (
          <p className="painel__ajuda">
            Previsão de resposta: até {formatarData(dados.prazoAnalise)}.
          </p>
        ) : null}

        <div className="rodape">
          <button type="button" className="botao" onClick={aoVoltar}>
            Voltar ao início
          </button>
        </div>
      </div>
    )
  }

  /* ---------------------------------------------------------------- código */
  if (fase === 'codigo') {
    return (
      <form onSubmit={entrar} noValidate>
        <div className="grade">
          <p className="painel__ajuda">
            Enviamos um código de 6 dígitos para o e-mail cadastrado desta empresa. Ele pode levar
            alguns minutos para chegar — confira também a caixa de spam.
          </p>

          <Campo
            rotulo="Código recebido"
            valor={codigo}
            aoMudar={(v) => setCodigo(v.replace(/\D/g, '').slice(0, 6))}
            destaque
            modo="numeric"
            maxLength={6}
            autoComplete="one-time-code"
            placeholder="000000"
          />
        </div>

        {erro ? (
          <p className="aviso aviso--erro" role="alert">
            {erro}
          </p>
        ) : null}

        <div className="rodape">
          <button
            type="button"
            className="botao"
            onClick={() => {
              setFase('cnpj')
              setCodigo('')
              setErro(null)
              setToken(null)
            }}
          >
            Usar outro CNPJ
          </button>
          <button
            type="submit"
            className="botao botao--principal"
            aria-disabled={ocupado || codigo.length < 6}
          >
            {ocupado ? 'Entrando…' : 'Ver minha solicitação'}
          </button>
        </div>
      </form>
    )
  }

  /* ------------------------------------------------------------------ CNPJ */
  return (
    <form onSubmit={pedirCodigo} noValidate>
      <div className="grade">
        <Campo
          rotulo="CNPJ"
          valor={cnpj}
          aoMudar={(v) => {
            setCnpj(v)
            if (erro) setErro(null)
          }}
          aoSair={() => {
            if (validarCnpj(cnpjNorm)) setCnpj(formatarCnpj(cnpjNorm))
          }}
          destaque
          largo
          modo="text"
          maxLength={18}
          placeholder="00.000.000/0000-00"
          dica="Enviaremos um código para o e-mail que já está cadastrado nesta solicitação."
        />
      </div>

      <div className="turnstile">
        <Turnstile
          ref={turnstile}
          sitekey={TURNSTILE_SITEKEY}
          action={ACAO_ACESSO}
          aoObterToken={(t) => {
            setToken(t)
            if (t && erro) setErro(null)
          }}
          aoFalhar={(m) => setErro(m)}
        />
      </div>

      {erro ? (
        <p className="aviso aviso--erro" role="alert">
          {erro}
        </p>
      ) : null}

      <div className="rodape">
        <button type="button" className="botao" onClick={aoVoltar}>
          Voltar
        </button>
        <button type="submit" className="botao botao--principal" aria-disabled={ocupado}>
          {ocupado ? 'Enviando…' : 'Receber código'}
        </button>
      </div>
    </form>
  )
}
